"use client"
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import ContactModal from './ContactModal'
import { useRouter } from 'next/navigation'
import InvoiceHeader from './InvoiceHeader'
import InvoiceTable from './InvoiceTable'
import InvoiceSummary from './InvoiceSummary'
import { updateInvoiceForm } from './../redux/reducers/invoiceSlice'
import OverallChargesModal from './OverallChargesModal'
import ItemDescModalSSG from './ItemDescModalSSG'
import InvoiceFormClientHandler from './InvoiceFormClientHandler'
import InvoiceChangeHandler from './InvoiceChangeHandler'

const date = new Date()
date.setDate(date.getDate() + 7) // add 7 days
const sevenDaysAfter = date.toISOString().split('T')[0]

const today = new Date().toISOString().split('T')[0]

export default function InvoiceForm({ data }) {
  const reduxInvoiceItems = useSelector((state) => state.invoice.formFields)
  const { selectedTemplateId } = useSelector(state => state.invoice)
  
  const [allRows, setAllRows] = useState(() => reduxInvoiceItems?.allItems?.length > 0 ? reduxInvoiceItems?.allItems : 
    data?.metadata?.pre_filled_invoice_items?.map(item => ({
      "item-quantity-placeholder": item?.quantity,
      "item-unit-price-placeholder": item?.unitPrice,
      "item-name": item?.description,
      "item-quantity": "",
      "item-unit-price": "",
      "item-description": "",
      "item-tax": "",
      "item-tax-type": "percentage",
      "tax-fixed-value": "",
      "subtotal": "",
      "hasErrors": false
    })))

  const [invoiceData, setInvoiceData] = useState({
    invoiceNo: reduxInvoiceItems?.invoiceNo || '001',
    issueDateValue: reduxInvoiceItems?.issueDateValue || today,
    dueDateValue: reduxInvoiceItems?.dueDateValue || sevenDaysAfter,
    senderDetails: {
      name: reduxInvoiceItems?.senderDetails?.name || '',
      extraFields: reduxInvoiceItems?.senderDetails?.extraFields || []
    },
    recepientDetails: {
      name: reduxInvoiceItems?.recepientDetails?.name || '',
      extraFields: reduxInvoiceItems?.recepientDetails?.extraFields || []
    },
    invoiceSummary: {
      summarySubtotal: reduxInvoiceItems?.invoiceSummary?.summarySubtotal || 0,
      totalTax: reduxInvoiceItems?.invoiceSummary?.totalTax || 0,
      finalPrice: reduxInvoiceItems?.invoiceSummary?.finalPrice || 0
    },
    invoiceTerms: reduxInvoiceItems?.invoiceTerms || ''
  })
  const [showContactModalType, setShowContactModalType] = useState(null)
  const [terms, setTerms] = useState(reduxInvoiceItems?.invoiceTerms || '')
  const [formErrors, setFormErrors] = useState([])
  const [showOverallChargesModal, setShowOverallChargesModal] = useState(false)
  const [overAllCharges, setOverAllCharges] = useState(() =>
    reduxInvoiceItems?.overAllCharges || [
    {
      name: '',
      placeholder: 'Transportation Fee, Discount, etc.',
      isDiscount: false,
      value: 0.00
    }
  ])

  const dispatch = useDispatch()
  const router = useRouter()

  const setAllRowsWithErrors = (index) => {
    const rows = [...allRows]
    rows[index].hasErrors = true
    setAllRows(rows)
  }

  const clearAllRowsErrors = () => {
    const rows = [...allRows]
    const newRows = rows.map(row => ({...row, hasErrors: false}))
    setAllRows(newRows)
  }

  const handleSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault()

    const errorList = []

    allRows.forEach((row, index) => {
      if (!row['item-name']) {
        errorList.push('Item name is required')
        setAllRowsWithErrors(index)
      }
      if (!row['item-quantity']) {
        errorList.push('Item quantity is required')
        setAllRowsWithErrors(index)
      }
      if (row['item-quantity'] < 1) {
        errorList.push('Item quantity must be more than 0')
        setAllRowsWithErrors(index)
      }
      if (!row['item-unit-price']) {
        errorList.push('Item unit price is required')
        setAllRowsWithErrors(index)
      }
      if (row['item-unit-price'] < 1) {
        errorList.push('Item unit price must be more than 0')
        setAllRowsWithErrors(index)
      }
    })

    if (errorList.length === 0) clearAllRowsErrors()

    if (!invoiceData?.invoiceNo) errorList.push('Invoice number is required')
    if (!invoiceData?.issueDateValue) errorList.push('Issue date is required')
    if (!invoiceData?.dueDateValue) errorList.push('Due date is required')
    if (!invoiceData?.senderDetails?.name) errorList.push('Sender name is required')
    if (!invoiceData?.recepientDetails?.name) errorList.push('Recepient name is required')
    
    setFormErrors(errorList)

    if (errorList.length > 0) return

    const summarySubtotal = allRows.reduce(
      (total, item) => total + item['item-unit-price'] * item['item-quantity'], 0)
    
    const totalTax = allRows.reduce((total, item) => {
      const currentTax =
        item['item-tax-type'] === 'percentage'
          ? (item['item-tax'] * item['item-quantity'] * item['item-unit-price']) / 100
          : item['item-tax'] * item['item-quantity']
      return total + currentTax
    }, 0)

    const totalCharges = overAllCharges?.reduce((total, charge) => {
      if (!charge.name || !charge.value) return total

      return total + (charge.isDiscount ? -charge.value : charge.value)
    }, 0) || 0

    const invoiceSummary = {
      ...invoiceData?.invoiceSummary,
      currency: reduxInvoiceItems?.invoiceSummary?.currency || 'USD',
      summarySubtotal: summarySubtotal,
      totalTax: totalTax,
      finalPrice: summarySubtotal + totalTax + totalCharges
    }
    
    const invoiceObj = {
      ...invoiceData,
      allItems: allRows,
      invoiceTerms: terms,
      overAllCharges: overAllCharges,
      invoiceSummary
    }

    dispatch(updateInvoiceForm(invoiceObj))
    // Google Tag Manager - Track invoice creation conversion
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        'event': 'invoice_created',
        'event_category': 'engagement',
        'event_label': 'invoice',
        'value': invoiceData?.invoiceSummary?.finalPrice,
        'currency': invoiceData?.invoiceSummary?.currency,
        'invoice_type': 'invoice',
        'invoice_amount': invoiceData?.invoiceSummary?.finalPrice
      })
    }
    if (selectedTemplateId) router.push(`/invoice/download/download-${data?.intent_key}`)
    else router.push(`/invoice/choose/choose-${data?.intent_key}`) 
  }

  return (
    <main className="mt-7">
      <form id="invoice-form" onSubmit={handleSubmit} className="max-w-5xl mx-auto sm:p-8 bg-white shadow-2xl rounded-2xl border border-gray-100">
        {/* Header Section */}
        <header className="sm:flex justify-between items-start sm:mb-12 p-8 sm:p-0">
          <section className="flex-1 mb-12 sm:mb-0" aria-label="Logo Upload">
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors cursor-pointer group">
              <div className="text-gray-400 group-hover:text-blue-400 transition-colors">
                <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                </svg>
                <label htmlFor="logo-upload" className="text-sm font-medium">Add Your Logo (Coming Soon)</label>
              </div>
            </div>
          </section>
          <InvoiceHeader invoiceData={invoiceData} setInvoiceData={setInvoiceData} />
        </header>

        {/* Contact Information */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:mb-12 mb-6 sm:p-0" aria-label="Contact Information">
          {/* From Section */}
          <section className="sm:space-y-4" aria-label="Contact Information for Freelancer">
            <h3 className="text-lg font-semibold text-gray-800 ml-6 sm:ml-2">From</h3>
            <button onClick={() => setShowContactModalType("from")} type="button" className="flex items-center space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer border-2 border-transparent hover:border-blue-200 contact-trigger w-full text-left appearance-none min-h-32" data-type="from">
              <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="sender-info">
                  <p id='sender-name-placeholder' className={`font-medium text-gray-400 ${invoiceData?.senderDetails?.name ? 'text-gray-600' : 'text-gray-400'}`}>
                    {invoiceData?.senderDetails?.name 
                    || 
                    data?.metadata?.form_labels?.senderName 
                    || 
                    "Sender's Name"}
                  </p>
                  {invoiceData?.senderDetails?.extraFields?.length > 0 ? (
                    invoiceData?.senderDetails?.extraFields?.map((key, index) => (
                      <p key={index} className="text-sm text-gray-600 truncate">
                        {key?.value}
                      </p>
                    ))
                  ) : (
                  <p id='sender-contact-placeholder' className="text-sm text-gray-400 mt-1">
                    {`${data?.metadata?.form_labels?.senderContact} Here`}
                  </p>
                  )}
                </div>
              </div>
              <svg className="hidden sm:block w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </section>

          {/* To Section */}
          <section className="sm:space-y-4" aria-label="Contact Information for Client">
            <h3 className="text-lg font-semibold text-gray-800 ml-6 sm:ml-2">To</h3>
            <button onClick={() => setShowContactModalType("to")} type="button" className="flex items-center space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer border-2 border-transparent hover:border-blue-200 contact-trigger w-full text-left appearance-none min-h-32" data-type="to">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="recipient-info">
                  <p id='client-name-placeholder' className={`font-medium text-gray-400 ${invoiceData?.recepientDetails?.name ? 'text-gray-600' : 'text-gray-400'}`}>{
                    invoiceData?.recepientDetails?.name 
                    || 
                    data?.metadata?.form_labels?.recepientName 
                    || 
                    "Recepient's Name"}</p>
                  {invoiceData?.recepientDetails?.extraFields?.length > 0 ? (
                    invoiceData?.recepientDetails?.extraFields?.map((key, index) => (
                      <p key={index} className="text-sm text-gray-600 truncate">
                        {key?.value}
                      </p>
                    ))
                  ) : (
                    <p id='client-contact-placeholder' className="text-sm text-gray-400 mt-1">
                      {`${data?.metadata?.form_labels?.recepientContact} Here`}
                    </p>
                  )}
                </div>
              </div>
              <svg className="hidden sm:block w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </section>
        </section>

        {/* Invoice Table */}
        <section aria-label="Invoice Items">
          <InvoiceTable allRows={allRows} setAllRows={setAllRows}/>
        </section>

        {/* Invoice Summary */}
        <aside className="sm:flex justify-end items-center mt-8 px-4 sm:px-0">
          <div id="form-errors" className={`mr-auto ${formErrors.length > 0 ? 'block' : 'hidden'}`}>
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div className="ml-3">
                <h2 className="text-sm font-medium text-red-800">Please fix the following errors:</h2>
                <ul id='error-list' className="mt-2 text-sm text-red-700 list-disc list-inside">
                  {formErrors.map((error, index) => <li key={index} className='field-error'>{error}</li>)}
                </ul>
              </div>
            </div>
          </div>
        <InvoiceSummary allRows={allRows} setShowOverallChargesModal={setShowOverallChargesModal} overAllCharges={overAllCharges} />
      </aside>
      <div className="flex justify-end px-4 sm:px-0">
        <button type="submit" className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer">
          Create Invoice now
        </button>
      </div>

      {/* Terms Section */}
      <section className="mt-12 pt-8 border-t border-gray-200">
        <div className="bg-gray-50 rounded-xl p-6">
          <label htmlFor='invoice-terms' className="block text-sm font-medium text-gray-700 mb-2">
            Invoice terms
          </label>
          <textarea
            name="invoice-terms"  
            id='invoice-terms'
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            placeholder="Add your payment terms, conditions, or notes here..."
          />
        </div>
      </section>
    </form>
    {showContactModalType && (
      <ContactModal 
        showContactModalType={showContactModalType} 
        setShowContactModalType={setShowContactModalType} 
        invoiceData={invoiceData}
        setInvoiceData={setInvoiceData} 
      />
      )}
      {showOverallChargesModal && (
        <OverallChargesModal 
          setShowOverallChargesModal={setShowOverallChargesModal} 
          overAllCharges={overAllCharges}
          setOverAllCharges={setOverAllCharges}
        />
      )}
    </main>
  )
}